import type { CatalogV2Channel } from '@skupreme/api-contracts/core';
import type { BadgeTone } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { countryOfMarketplace, MARKET_REGIONS, NO_COUNTRY } from './markets';

/** The select value that stands for "no filter". */
export const ALL = 'all';
/** The activity select's value for the API default (active items): nothing is sent. */
export const DEFAULT_ACTIVITY = 'active';

export const V2_STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'discontinued', label: 'Discontinued' },
  /* EN-4396 Archive (Ely 10-20): not active, or no sale or channel listing for 120 days (the API's liveness rule). */
  { value: 'archived', label: 'Archived (inactive or unsold 120 days)' },
  { value: 'active,discontinued,archived', label: 'All states' },
];

/* M2 listing hygiene (Ely 11-01): Active = an Active listing, a sale in 120 days, made by hand, or no listing status known. */
export const V2_ACTIVITY_OPTIONS = [
  { value: DEFAULT_ACTIVITY, label: 'Active listings' },
  { value: 'not_active', label: 'Not active' },
  { value: 'all', label: 'Active and not active' },
];

export const V2_TYPE_OPTIONS = [
  { value: ALL, label: 'All types' },
  { value: 'physical', label: 'Physical' },
  { value: 'bundle', label: 'Bundle' },
];

const STATE_TONES: Record<string, BadgeTone> = {
  active: 'success',
  discontinued: 'caution',
  archived: 'neutral',
};

const FULFILLMENT_LABELS: Record<string, string> = {
  fba: 'FBA',
  wfs: 'WFS',
};

/** The state filter value of the Archived view. */
export const ARCHIVED_VIEW = 'archived';

export const typeLabel = (type: string) =>
  type === 'bundle' ? 'Bundle' : 'Physical';

export const stateTone = (state: string): BadgeTone =>
  STATE_TONES[state] ?? 'neutral';

export const stateLabel = (state: string) => CommonUtility.toCapitalize(state);

/** FBA or WFS; merchant-fulfilled listings carry no label. */
export const fulfillmentLabel = (channel: CatalogV2Channel) =>
  FULFILLMENT_LABELS[channel.fulfillment_channel ?? ''] ?? null;

/* A connection name that repeats its first word ("Amazon Amazon North America") reads once. */
const REPEATED_FIRST_WORD = /^(\S+)\s+\1(?=\s|$)/i;

export const channelLabel = (
  channel: Pick<CatalogV2Channel, 'display_name' | 'provider'>,
) =>
  (channel.display_name || channel.provider || 'Unknown channel').replace(
    REPEATED_FIRST_WORD,
    '$1',
  );

/** The marketplace type the shared icons are keyed by ('amazon' for amazon_sp), as the V2 orders page maps it. */
export const channelMarketplaceType = (provider: string) => {
  if (provider === 'amazon' || provider.startsWith('amazon_')) return 'amazon';
  if (provider.startsWith('shopify')) return 'shopify';
  if (provider.startsWith('walmart')) return 'walmart';
  if (provider.startsWith('ebay')) return 'ebay';
  return provider;
};

export type MarketplaceChannel = {
  /** `connectionId:country`, `connectionId:none` for the entry with no country. */
  key: string;
  connectionId: string;
  provider: string;
  label: string;
  /** M2: the ISO country these listings sell in; null for listings with no market or only '' (stores) markets. */
  country: string | null;
  isSeed: boolean;
  /** FBA / WFS, once each, in first-seen order. */
  fulfilledBy: string[];
  listings: number;
  /** M2: each listing's markets in this country, in listing order; undefined for a listing from an API that sends none. */
  listingMarkets: CatalogV2Channel['markets'][];
};

type ListingMarkets = CatalogV2Channel['markets'];

/* A listing's markets by country (null: '' and unknown ids); a listing with none stays whole under null. */
const marketsByCountry = (markets: ListingMarkets) => {
  const byCountry = new Map<string | null, ListingMarkets>();
  if (!markets?.length) return byCountry.set(null, markets);
  for (const market of markets) {
    const country = countryOfMarketplace(market.marketplace_id);
    byCountry.set(country, [...(byCountry.get(country) ?? []), market]);
  }
  return byCountry;
};

const COUNTRY_ORDER = MARKET_REGIONS.flatMap(region => region.countries);

/* The picker's country order; no country last. */
const countryRank = (country: string | null) =>
  country === null ? COUNTRY_ORDER.length : COUNTRY_ORDER.indexOf(country);

/**
 * The item's marketplaces, one per connection and country (M2: one Amazon North America connection sells in US, CA and
 * MX, each its own logo): whether one of its listings is the seed, how they are fulfilled, how many there are. A
 * removed market counts toward its country. Storage-provider listings are left to the item page. In first-seen
 * connection order, then the picker's country order.
 */
export const marketplaceChannels = (
  channels: CatalogV2Channel[],
): MarketplaceChannel[] => {
  const byConnection = new Map<string, Map<string, MarketplaceChannel>>();
  for (const channel of channels) {
    if (channel.source_kind !== 'marketplace') continue;
    const entries =
      byConnection.get(channel.connection_id) ??
      new Map<string, MarketplaceChannel>();
    byConnection.set(channel.connection_id, entries);
    const fulfilledBy = fulfillmentLabel(channel);
    for (const [country, markets] of marketsByCountry(channel.markets)) {
      const key = `${channel.connection_id}:${country ?? NO_COUNTRY}`;
      const entry: MarketplaceChannel = entries.get(key) ?? {
        key,
        connectionId: channel.connection_id,
        provider: channel.provider ?? '',
        label: channelLabel(channel),
        country,
        isSeed: false,
        fulfilledBy: [],
        listings: 0,
        listingMarkets: [],
      };
      entry.listings += 1;
      entry.listingMarkets.push(markets);
      entry.isSeed ||= channel.is_seed;
      if (fulfilledBy && !entry.fulfilledBy.includes(fulfilledBy)) {
        entry.fulfilledBy.push(fulfilledBy);
      }
      entries.set(key, entry);
    }
  }
  return [...byConnection.values()].flatMap(entries =>
    [...entries.values()].sort(
      (a, b) => countryRank(a.country) - countryRank(b.country),
    ),
  );
};

/* The region a connection is named for ("Amazon North America"): the country shown instead says where. */
const REGION_WORDS = /\s+(North America|Europe|Far East)\b/gi;

/** What a marketplace's logo says on hover: "Amazon · US · Seed · FBA · 19 listings". */
export const marketplaceChannelText = (channel: MarketplaceChannel) =>
  [
    (channel.country && channel.label.replace(REGION_WORDS, '')) ||
      channel.label,
    channel.country,
    channel.isSeed && 'Seed',
    ...channel.fulfilledBy,
    channel.listings > 1 && `${channel.listings} listings`,
  ]
    .filter(Boolean)
    .join(' · ');

/** EN-4134: a channel's price as read ("$229.99"), or null when it gave none; an unknown currency reads as USD. */
export const formatV2Price = (
  price: { amount: number | null; currency: string | null } | null | undefined,
) => {
  if (!price || price.amount === null) return null;
  const currency =
    price.currency &&
    Intl.supportedValuesOf('currency').includes(price.currency)
      ? price.currency
      : 'USD';
  return CommonUtility.currencyFormat(price.amount, currency);
};

/**
 * EN-4134: where a listing lives on its marketplace, when its ids are enough to say: Amazon's Seller Central inventory
 * searched by the seller SKU, eBay's item page. Null otherwise (Shopify and Walmart need the store's domain, which V2
 * does not hold).
 */
export const listingUrl = (listing: {
  provider: string | null;
  source_kind: string;
  external_id: string;
  sku: string | null;
}) => {
  if (listing.source_kind !== 'marketplace') return null;
  const type = channelMarketplaceType(listing.provider ?? '');
  if (type === 'amazon') {
    const sku = listing.sku ?? listing.external_id;
    return `https://sellercentral.amazon.com/myinventory/inventory?searchField=all&searchTerm=${encodeURIComponent(sku)}`;
  }
  if (type === 'ebay') {
    return `https://www.ebay.com/itm/${encodeURIComponent(listing.external_id)}`;
  }
  return null;
};

/** A null count is "no inventory position", not zero. */
export const formatAvailable = (available: number | null) =>
  available === null ? '—' : CommonUtility.numberWithCommas(available);
